import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TimeSeriesPivotTableDataService } from '@backoffice-frontend/controlling/domain';
import { UpdateDialogParams } from '@clean-code/models';
import { ActionButtonModule } from '@clean-code/shared/components/ui-action-button';
import { TranslocoModule, TranslocoPipe } from '@jsverse/transloco';
import dayjs from 'dayjs';
import { DxDateBoxModule } from 'devextreme-angular';
import { tap } from 'rxjs';
import { PivotTableFunctionsService } from '../../data/functions/pivot-table-functions.service';
import { PivotTableService } from '../../data/pivot-table.service';
import { CellHistoryComponent } from '../cell-history/cell-history.component';

@Component({
  selector: 'feature-pivot-table-cell-dialog',
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    TranslocoModule,
    TranslocoPipe,
    CellHistoryComponent,
    ActionButtonModule,
    MatIcon,
    MatIconButton,
    DxDateBoxModule,
  ],
  templateUrl: './cell-dialog.component.html',
  styleUrls: ['./cell-dialog.component.scss'],
})
export class CellDialogComponent {
  public dialogParams: UpdateDialogParams = inject(MAT_DIALOG_DATA);
  public dialogRef = inject(MatDialogRef<CellDialogComponent>);
  protected timeSeriesPivotTableService = inject(
    TimeSeriesPivotTableDataService,
  );
  protected tableService = inject(PivotTableService);
  protected pivotTableFunctionsService = inject(PivotTableFunctionsService);

  public cellFormControl = new FormControl(this.dialogParams.cellValue);
  public timestampFormControl = new FormControl<Date | string>(
    this.dialogParams.data.timestamp,
  );

  title: string =
    this.dialogParams.data.identifier +
    ', ' +
    dayjs(this.dialogParams.data.tradeDate).format('YYYY-MM-DD');
  // columnName: string = this.dialogParams.columnName;
  columnName: string =
    this.pivotTableFunctionsService.columnNameToTranslationKey(
      this.dialogParams.columnName,
    );
  initialValue = this.dialogParams.cellValue;
  private initialTimestamp: Date | string = this.dialogParams.data.timestamp;

  isButtonDisabled = true;

  constructor() {
    this.timestampFormControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updateButtonState());
  }

  updateCell() {
    const idParam = this.dialogParams.data.id;
    const columnName = this.dialogParams.columnName;
    const value = this.cellFormControl.value;
    const parsedTimestamp = dayjs(this.timestampFormControl.value);
    const timestamp =
      this.timestampFormControl.value && parsedTimestamp.isValid()
        ? parsedTimestamp.toDate()
        : null;
    this.timeSeriesPivotTableService
      .update$(idParam, columnName, +value, timestamp)
      .pipe(
        tap(() => {
          this.tableService.setRefreshTable(true);
          this.dialogRef.close();
        }),
      )
      .subscribe();
  }

  deleteCell() {
    const idParam = this.dialogParams.data.id;
    this.timeSeriesPivotTableService
      .delete$(idParam)
      .pipe(
        tap(() => {
          this.tableService.setRefreshTable(true);
          this.dialogRef.close();
        }),
      )
      .subscribe();
  }

  onInput() {
    this.updateButtonState();
  }

  onClearButtonClick() {
    this.cellFormControl.setValue(null);
    this.updateButtonState();
  }

  private updateButtonState() {
    const valueChanged = +this.cellFormControl.value !== this.initialValue;
    const timestampChanged = !dayjs(this.timestampFormControl.value).isSame(
      dayjs(this.initialTimestamp),
    );
    this.isButtonDisabled = !valueChanged && !timestampChanged;
  }
}
